<script setup lang="ts">
import { Key } from "ts-key-enum"
import { computed, nextTick, ref, watch } from "vue"
import { useSnackbar } from "@/utils/useSnackbar"

interface Props {
  modelValue: string
  editable?: boolean
  editing: boolean
}

interface Emits {
  (event: "update:modelValue", newName: string): void
  (event: "update:editing", value: boolean): void
}

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<Props>(), { editable: true })
const emit = defineEmits<Emits>()

const name = ref<string>(props.modelValue ?? "")

watch(
  () => props.modelValue,
  () => {
    name.value = props.modelValue
  }
)

const editing = defineModel<boolean>("editing", { default: false })

const inputRef = ref<HTMLInputElement>()
const { addSnackbar } = useSnackbar()

async function focusInput() {
  await nextTick()

  if (inputRef.value) {
    inputRef.value.focus()
    inputRef.value.select()
  }
}

// @keydown.stop="handleKey" This captures all key events, no bubbling
function handleKey(e: KeyboardEvent) {
  switch (e.key) {
    case Key.Escape:
      editing.value = false
      name.value = props.modelValue
      break
    case Key.Enter:
      updateValue()
      break
    case Key.ArrowDown:
    case Key.ArrowUp:
      editing.value = false
      break
    case Key.ArrowLeft:
      if (inputRef.value?.selectionStart === 0 && inputRef.value?.selectionEnd === 0) {
        editing.value = false
      }
      break
    case Key.ArrowRight:
      if (inputRef.value?.selectionStart === name.value.length) {
        editing.value = false
      }
      break
    default:
      break
  }

  e.stopPropagation()
}

function updateValue() {
  if (!editing.value) {
    return
  }

  if (props.modelValue === name.value) {
    editing.value = false
    return
  }

  if (name.value === "") {
    addSnackbar({
      type: "error",
      message: "Not valid a name",
    })
    editing.value = false
    name.value = props.modelValue
    return
  }

  name.value = name.value?.trim()

  emit("update:modelValue", name.value)

  editing.value = false
}

async function handleDoubleClick() {
  if (props.editable) {
    editing.value = true
    await focusInput()
  }
}

const canEdit = computed(() => {
  if (!props.editable) {
    return false
  }
  return editing.value
})

watch(editing, async () => {
  if (editing.value) {
    editing.value = true
    await focusInput()
  }

  emit("update:editing", editing.value)
})
</script>

<template>
  <div
    v-if="!canEdit"
    class="flex grow truncate"
    :class="[{ 'select-none': !editing }]"
    v-bind="$attrs"
    @dblclick="handleDoubleClick"
  >
    {{ name }}
  </div>
  <input
    v-else
    ref="inputRef"
    v-model="name"
    type="text"
    class="flex rounded-md bg-transparent"
    v-bind="$attrs"
    @keydown="handleKey"
    @blur="updateValue"
  >
</template>
